/**
 * The chart, validated once at construction so `postDocument` can trust it.
 */
import { raise } from "../errors/index.js";
import { getCurrency } from "../money/currency.js";
import { NORMAL_SIDE } from "./account.js";
/**
 * Refuses, with `CHART_INVALID`, a chart that would let a wrong posting
 * through or a statement go wrong silently:
 *
 * - duplicate ids or codes;
 * - a postable header (an account that is somebody's parent) — the
 *   double-count-in-every-roll-up defect, docs/22 §2;
 * - a parent that does not exist, or a parent chain that loops;
 * - a stored normal side that disagrees with the kind, unless `contra`;
 * - a declared currency the money table does not know.
 */
export function buildChart(accounts) {
    const byId = new Map();
    const byCode = new Map();
    for (const account of accounts) {
        if (byId.has(account.id))
            raise("CHART_INVALID", { code: account.code, reason: `duplicate id ${account.id}` });
        if (byCode.has(account.code))
            raise("CHART_INVALID", { code: account.code, reason: "duplicate code" });
        if (account.normalSide !== NORMAL_SIDE[account.kind] && account.contra !== true) {
            raise("CHART_INVALID", { code: account.code, reason: `${account.kind} account stored on the ${account.normalSide} side` });
        }
        if (account.currency !== undefined)
            getCurrency(account.currency);
        byId.set(account.id, account);
        byCode.set(account.code, account);
    }
    const parents = new Set();
    for (const account of accounts) {
        if (account.parent === undefined)
            continue;
        if (!byId.has(account.parent)) {
            raise("CHART_INVALID", { code: account.code, reason: `parent ${account.parent} is not in the chart` });
        }
        parents.add(account.parent);
        // Walk up; a chart of a few hundred accounts makes this trivially cheap.
        const seen = new Set([account.id]);
        let cursor = byId.get(account.parent);
        while (cursor !== undefined) {
            if (seen.has(cursor.id))
                raise("CHART_INVALID", { code: account.code, reason: "parent chain loops" });
            seen.add(cursor.id);
            cursor = cursor.parent === undefined ? undefined : byId.get(cursor.parent);
        }
    }
    for (const id of parents) {
        const header = byId.get(id);
        if (header.postable)
            raise("CHART_INVALID", { code: header.code, reason: "a header account must not be postable" });
    }
    return {
        accounts,
        byId: (id) => byId.get(id),
        byCode: (code) => byCode.get(code),
    };
}
/**
 * Postable accounts with no FS1/FS2 row. Gate 22: an unmapped account
 * *refuses* the Order 361 pack rather than shrinking it. Headers are
 * excluded because a header carries no balance of its own.
 */
export function unmappedAccounts(chart) {
    return chart.accounts.filter((account) => account.postable && account.fsMapping === undefined);
}
//# sourceMappingURL=chart.js.map